import type { Lead } from '@keyvue/one-api-client';
import { computed, type ComputedRef } from 'vue';
import useClient from '@composables/useClient';
import { Role } from '@keyvue/one-api-client';

interface LeadSecurity {
    isUserOfAssignedCompany: ComputedRef,
    canView: ComputedRef,
    canViewDetail: ComputedRef,
    canChangeReferrer: ComputedRef,
    canReopen: ComputedRef,
}

export default function useLeadSecurity(): { forLead: (lead: Lead) => LeadSecurity };
export default function useLeadSecurity(composedLead): LeadSecurity;

export default function useLeadSecurity(composedLead: Lead | null = null) {
    const { currentUser, currentCompany, currentLegacyCompany } = useClient();

    const forLead = (lead: Lead) => {
        const isUserOfAssignedCompany = computed(() => (!currentCompany.value ? false : lead.assignee.company.equals(currentCompany.value)));

        const canView = computed(() => {
            if (!currentCompany.value) {
                return false;
            }

            const referredByCompany = lead.referrer.company;
            const assignedToCompany = lead.assignee.company;

            return referredByCompany.equals(currentCompany.value) || assignedToCompany.equals(currentCompany.value);
        });

        const canViewDetail = computed(() => {
            if (!currentCompany.value || !currentUser.value) {
                return false;
            }

            if (currentUser.value.role.equals(Role.COMPANY_MANAGER)) {
                // For company managers, the user must be in the referred by or assigned to company
                const referredByCompany = lead.referrer.company;
                const assignedToCompany = lead.assignee.company;

                if (referredByCompany.equals(currentCompany.value) || assignedToCompany.equals(currentCompany.value)) {
                    return true;
                }
            }

            if (currentUser.value.role.equals(Role.TEAM_MANAGER) || currentUser.value.role.equals(Role.TEAM_LEAD)) {
                // For team managers and team leads, the user must be in the referred by or assigned to team
                if (currentUser.value.teams.some((team) => {
                    const referredByTeam = lead.referrer.team;
                    const assignedToTeam = lead.assignee.team;

                    return referredByTeam.equals(team) || assignedToTeam.equals(team);
                })) {
                    return true;
                }
            }

            // For employees, the user must be the referred by or assigned to user
            if (currentUser.value.role.equals(Role.EMPLOYEE)) {
                const referredByUser = lead.referrer.user;
                const assignedToUser = lead.assignee.user;

                if (referredByUser?.equals(currentUser.value) || assignedToUser?.equals(currentUser.value)) {
                    return true;
                }
            }

            // Check additional permissions (if any)
            if (lead.permissions) {
                // Check additional user permissions
                if (lead.permissions.userIds.includes(currentUser.value.id.id)) {
                    return true;
                }

                // Check additional team permissions
                if (currentUser.value.teams.some((team) => lead?.permissions?.teamIds.includes(team.id.id))) {
                    return true;
                }
            }

            // Not allowed
            return false;
        });

        const canChangeReferrer = computed(() => {
            if (!currentUser.value || !isUserOfAssignedCompany.value) {
                return false;
            }

            /**
             * @todo Needs refactoring
             * Because we have legacy Role values stored in Company.permissions, we need to convert them when comparing
             * against new One roles. This should be removed once we have proper Settings in place (tm).
             */
            if (currentLegacyCompany.value && currentLegacyCompany.value.permissions) {
                const roleMapping = new Map<string, string>([
                    [Role.EMPLOYEE.toString(), 'employee'],
                    [Role.TEAM_LEAD.toString(), 'team-lead'],
                    [Role.TEAM_MANAGER.toString(), 'branch-manager'],
                    [Role.COMPANY_MANAGER.toString(), 'company-manager'],
                ]);

                // convert role to legacy role
                const legacyRole = roleMapping.get(currentUser.value.role.toString());

                return !!currentLegacyCompany.value.permissions
                    .find((permission) => permission.name === 'referral.referredBy')?.roles
                    .map((role) => role.toString())
                    .includes(legacyRole || '');
            }

            return false;
        });

        const canReopen = computed(() => {
            if (!isUserOfAssignedCompany.value || !lead.status.isFinal || !currentUser.value) {
                return false;
            }

            const roleMapping = new Map<string, string>([
                [Role.EMPLOYEE.toString(), 'employee'],
                [Role.TEAM_LEAD.toString(), 'team-lead'],
                [Role.TEAM_MANAGER.toString(), 'branch-manager'],
                [Role.COMPANY_MANAGER.toString(), 'company-manager'],
            ]);

            const legacyRole = roleMapping.get(currentUser.value.role.toString());

            return !!currentLegacyCompany.value.permissions
                .find((permission) => permission.name === 'referral.reopen')?.roles
                .map((role) => role.toString())
                .includes(legacyRole || '');
        });

        return {
            isUserOfAssignedCompany,
            canView,
            canViewDetail,
            canChangeReferrer,
            canReopen,
        };
    };

    if (composedLead) {
        return forLead(composedLead);
    }

    return { forLead };
}
